import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_DIRECTION_ASC, TABLE_DIRECTION_DESC } from 'lib/tableLib'
import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import useSnackbar from '../Snackbar/useSnackbar'

const usePartnerInventoryFilters = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const trueOption = useMemo(
    () => getOption(t('common.filters.boolean.true'), 'true'),
    [t]
  )

  const falseOption = useMemo(
    () => getOption(t('common.filters.boolean.false'), 'false'),
    [t]
  )

  const nullOption = useMemo(
    () => getOption(t('common.filters.boolean.null'), 'null'),
    [t]
  )

  const defaultFilters = useMemo(
    () => ({
      searchQuery: '',
      plateQuery: '',
      // sessionIdQuery: '',
      sessionIdMin: 0,
      sessionIdMax: 0,
      storagePlaceQuery: '',
      daysMin: 0,
      daysMax: 0,
      previewDaysMin: 0,
      previewDaysMax: 0,
      priceMin: 0,
      priceMax: 0,
      withdrawalPenaltyMin: 0,
      withdrawalPenaltyMax: 0,
      feePreviewMin: 0,
      feePreviewMax: 0,
      restitutionPenaltyMin: 0,
      restitutionPenaltyMax: 0,
      assignedAtFrom: null,
      assignedAtTo: null,
      stateChangedAtFrom: null,
      stateChangedAtTo: null,
      expiresAtFrom: null,
      expiresAtTo: null,
      availabilityAtFrom: null,
      availabilityAtTo: null,
      restored: OPTION_ALL_VALUE,
      releaseFlag: OPTION_ALL_VALUE,
      mandatoryToBuy: OPTION_ALL_VALUE,
      storagePlaces: [OPTION_ALL_VALUE],
      partners: [OPTION_ALL_VALUE],
      brands: [OPTION_ALL_VALUE],
      overviewAudit: [OPTION_ALL_VALUE],
      models: [OPTION_ALL_VALUE],
      states: [OPTION_ALL_VALUE],
      order: { column: 'sessionId', direction: TABLE_DIRECTION_DESC },
      commentsQuery: '',
      releaseCommentsQuery: '',
    }),
    []
  )

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    error: null,
  })
  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    sessionIdMin: 0,
    sessionIdMax: 0,
    daysMin: 0,
    daysMax: 0,
    previewDaysMin: 0,
    previewDaysMax: 0,
    priceMin: 0,
    priceMax: 0,
    withdrawalPenaltyMin: 0,
    withdrawalPenaltyMax: 0,
    feePreviewMin: 0,
    feePreviewMax: 0,
    restitutionPenaltyMin: 0,
    restitutionPenaltyMax: 0,
    restored: [allOption, trueOption, falseOption, nullOption],
    releaseFlag: [allOption, trueOption, falseOption, nullOption],
    storagePlaces: [allOption],
    partners: [allOption],
    brands: [allOption],
    models: [allOption],
    overviewAudit: [allOption],
    states: [allOption],
    mandatoryToBuy: [allOption, trueOption, falseOption],
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error || null,
      }))
    },
    [setState]
  )

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        error: null,
      }))

      setFiltersData((s) => ({
        ...s,
        ...data,
        storagePlaces: [
          allOption,
          ...data?.storagePlaces.map((sp) => getOption(sp)),
        ],
        partners: [
          allOption,
          ...data?.partners.map((partner) =>
            getOption(partner.name, partner.id)
          ),
        ],
        brands: [allOption, ...data?.brands.map(getOption)],
        models: [allOption, ...data?.models.map(getOption)],
        overviewAudit: [
          allOption,
          ...data?.overviewAudit.map((x) =>
            getOption(t(`partnerInventory.overviewAudit.${x}`), x)
          ),
        ],
        states: [
          allOption,
          ...data?.states.map((state) =>
            getOption(t(`partnerInventory.status.${state}`), state)
          ),
        ],
      }))
      setFilters({
        ...defaultFilters,
        sessionIdMin: data.sessionIdMin,
        sessionIdMax: data.sessionIdMax,
        daysMin: data.daysMin,
        daysMax: data.daysMax,
        previewDaysMin: data.previewDaysMin,
        previewDaysMax: data.previewDaysMax,
        priceMin: data.priceMin,
        priceMax: data.priceMax,
        withdrawalPenaltyMin: data.withdrawalPenaltyMin,
        withdrawalPenaltyMax: data.withdrawalPenaltyMax,
        feePreviewMin: data.feePreviewMin,
        feePreviewMax: data.feePreviewMax,
        restitutionPenaltyMin: data.restitutionPenaltyMin,
        restitutionPenaltyMax: data.restitutionPenaltyMax,
        initialized: true,
      })
    },
    [allOption, defaultFilters, t]
  )
  const resetFilters = useCallback(() => {
    setFilters((s) => ({
      ...s,
      ...defaultFilters,
      sessionIdMin: filtersData.sessionIdMin,
      sessionIdMax: filtersData.sessionIdMax,
      daysMin: filtersData.daysMin,
      daysMax: filtersData.daysMax,
      previewDaysMin: filtersData.previewDaysMin,
      previewDaysMax: filtersData.previewDaysMax,
      priceMin: filtersData.priceMin,
      priceMax: filtersData.priceMax,
      withdrawalPenaltyMin: filtersData.withdrawalPenaltyMin,
      withdrawalPenaltyMax: filtersData.withdrawalPenaltyMax,
      feePreviewMin: filtersData.feePreviewMin,
      feePreviewMax: filtersData.feePreviewMax,
      restitutionPenaltyMin: filtersData.restitutionPenaltyMin,
      restitutionPenaltyMax: filtersData.restitutionPenaltyMax,
    }))
  }, [
    defaultFilters,
    filtersData.sessionIdMin,
    filtersData.sessionIdMax,
    filtersData.daysMax,
    filtersData.daysMin,
    filtersData.previewDaysMin,
    filtersData.previewDaysMax,
    filtersData.feePreviewMax,
    filtersData.feePreviewMin,
    filtersData.priceMax,
    filtersData.priceMin,
    filtersData.restitutionPenaltyMax,
    filtersData.restitutionPenaltyMin,
    filtersData.withdrawalPenaltyMax,
    filtersData.withdrawalPenaltyMin,
  ])

  const fetchFiltersData = useCallback(async () => {
    setStartFetching()

    try {
      const results = await axios.get(`${config.apiBaseUrl}filters/inventory`)

      setSuccessFetching(results.data.body)
    } catch (e) {
      let message =
        e.response?.data?.message ||
        t('common.error.fetchData', {
          attribute: e.response?.status,
        })

      setStopFetching(e)

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    }
  }, [
    setStartFetching,
    setStopFetching,
    setSuccessFetching,
    showSnackbarMessage,
    t,
  ])

  const initFilters = useCallback(async () => {
    await fetchFiltersData()
  }, [fetchFiltersData])

  const setSearchQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, searchQuery: value }))
    },
    [setFilters]
  )

  const setPlateQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, plateQuery: value }))
    },
    [setFilters]
  )

  const setCommentsQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, commentsQuery: value }))
    },
    [setFilters]
  )

  // REL-F03: search over the segregated "commenti Release" stream.
  const setReleaseCommentsQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, releaseCommentsQuery: value }))
    },
    [setFilters]
  )

  // const setSessionIdQuery = useCallback(
  //   (value) => {
  //     setFilters((s) => ({ ...s, sessionIdQuery: value }))
  //   },
  //   [setFilters]
  // )

  const setSessionIdRange = useCallback(
    (sessionIdMin, sessionIdMax) => {
      setFilters((s) => ({ ...s, sessionIdMin, sessionIdMax }))
    },
    [setFilters]
  )

  const setStoragePlaceQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, storagePlaceQuery: value }))
    },
    [setFilters]
  )

  const setStoragePlaces = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, storagePlaces: value }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setBrands = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, brands: value }))
    },
    [setFilters]
  )

  const setModels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, models: value }))
    },
    [setFilters]
  )

  const setOverviewAudit = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, overviewAudit: value }))
    },
    [setFilters]
  )

  const setStates = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, states: value }))
    },
    [setFilters]
  )

  const setDaysRange = useCallback(
    (daysMin, daysMax) => {
      setFilters((s) => ({ ...s, daysMin, daysMax }))
    },
    [setFilters]
  )

  const setPreviewDaysRange = useCallback(
    (previewDaysMin, previewDaysMax) => {
      setFilters((s) => ({ ...s, previewDaysMin, previewDaysMax }))
    },
    [setFilters]
  )

  const setPriceRange = useCallback(
    (priceMin, priceMax) => {
      setFilters((s) => ({ ...s, priceMin, priceMax }))
    },
    [setFilters]
  )

  const setWithdrawalPenaltyRange = useCallback(
    (withdrawalPenaltyMin, withdrawalPenaltyMax) => {
      setFilters((s) => ({ ...s, withdrawalPenaltyMin, withdrawalPenaltyMax }))
    },
    [setFilters]
  )

  const setFeePreviewRange = useCallback(
    (feePreviewMin, feePreviewMax) => {
      setFilters((s) => ({ ...s, feePreviewMin, feePreviewMax }))
    },
    [setFilters]
  )

  const setRestitutionPenaltyRange = useCallback(
    (restitutionPenaltyMin, restitutionPenaltyMax) => {
      setFilters((s) => ({
        ...s,
        restitutionPenaltyMin,
        restitutionPenaltyMax,
      }))
    },
    [setFilters]
  )

  const setAssignedAtRange = useCallback(
    (assignedAtFrom, assignedAtTo) => {
      setFilters((s) => ({ ...s, assignedAtFrom, assignedAtTo }))
    },
    [setFilters]
  )

  const setStateChangedAtRange = useCallback(
    (stateChangedAtFrom, stateChangedAtTo) => {
      setFilters((s) => ({ ...s, stateChangedAtFrom, stateChangedAtTo }))
    },
    [setFilters]
  )

  const setExpiresAtRange = useCallback(
    (expiresAtFrom, expiresAtTo) => {
      setFilters((s) => ({ ...s, expiresAtFrom, expiresAtTo }))
    },
    [setFilters]
  )

  const setAvailabilityAtRange = useCallback(
    (availabilityAtFrom, availabilityAtTo) => {
      setFilters((s) => ({ ...s, availabilityAtFrom, availabilityAtTo }))
    },
    [setFilters]
  )

  const setRestored = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, restored: value }))
    },
    [setFilters]
  )

  const setReleaseFlag = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, releaseFlag: value }))
    },
    [setFilters]
  )

  const setMandatoryToBuy = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, mandatoryToBuy: value }))
    },
    [setFilters]
  )

  const setSort = useCallback(
    (value) => {
      setFilters((s) => ({
        ...s,
        order: {
          column: value,
          direction:
            s.order.column !== value ||
            s.order.direction !== TABLE_DIRECTION_ASC
              ? TABLE_DIRECTION_ASC
              : TABLE_DIRECTION_DESC,
        },
      }))
    },
    [setFilters]
  )

  return {
    filters,
    filtersData,
    initFilters,
    setSearchQuery,
    setPlateQuery,
    setCommentsQuery,
    setReleaseCommentsQuery,
    // setSessionIdQuery,
    setSessionIdRange,
    setStoragePlaceQuery,
    setDaysRange,
    setPreviewDaysRange,
    setPriceRange,
    setWithdrawalPenaltyRange,
    setFeePreviewRange,
    setRestitutionPenaltyRange,
    setAssignedAtRange,
    setStateChangedAtRange,
    setExpiresAtRange,
    setAvailabilityAtRange,
    setRestored,
    setReleaseFlag,
    setStoragePlaces,
    setPartners,
    setBrands,
    setModels,
    setStates,
    setOverviewAudit,
    setSort,
    setMandatoryToBuy,
    resetFilters,
    initialized: filters.initialized,
    isFetching: state.isFetching,
    error: state.error,
  }
}
export default usePartnerInventoryFilters
